import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DataSnapshot } from "../lib/types";
import { loadGaps, loadOptimizations } from "../lib/api";
import { queryKeys } from "../lib/query-client";
import { navigateToSkill } from "../router";
import { BrandRail } from "../components/BrandRail";
import { FreshnessBar } from "../components/FreshnessBar";

interface Props {
  data: DataSnapshot;
}

type Tab = "overlaps" | "gaps" | "optimize";

export function Insights({ data }: Props) {
  const { stats, meta, skills } = data;
  const [tab, setTab] = useState<Tab>("overlaps");

  const { data: gaps, isLoading: gapsLoading } = useQuery({
    queryKey: queryKeys.gaps,
    queryFn: loadGaps,
  });
  const { data: opt } = useQuery({
    queryKey: queryKeys.optimizations,
    queryFn: loadOptimizations,
  });

  const issueCount = (stats.statusCounts?.warning ?? 0) + (stats.statusCounts?.critical ?? 0);

  const topOptimize = useMemo(() => {
    if (!opt?.bySkill) return [];
    return Object.entries(opt.bySkill)
      .sort((a, b) => a[1].score - b[1].score)
      .slice(0, 40);
  }, [opt]);

  const openSkill = (name: string) => {
    navigateToSkill(name);
  };

  const overlapCount = gaps?.overlaps?.length ?? 0;
  const gapCount = gaps?.gaps?.length ?? 0;
  const optimizeCount = topOptimize.length;
  const orphanCount = gaps?.orphans?.length ?? 0;

  const tabBtn = (key: Tab, label: string, count: number | string) => (
    <button
      key={key}
      type="button"
      onClick={() => setTab(key)}
      className={`font-mono text-[10px] uppercase tracking-[0.14em] px-4 py-2 border font-medium ${
        tab === key ? "bg-fg text-bg border-fg" : "border-fg text-muted hover:bg-[var(--hover)] hover:text-fg"
      }`}
    >
      {label} <span className="ml-1 opacity-70">({count})</span>
    </button>
  );

  return (
    <div className="grid min-h-screen" style={{ gridTemplateColumns: "220px 1fr" }}>
      <BrandRail counts={{ monitor: stats.total, issues: issueCount }} />
      <main className="min-w-0 pb-16">
        <FreshnessBar meta={meta} />

        <header className="px-6 pt-6 pb-4 border-b border-fg">
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
              // Portfolio intelligence · gaps + optimizer
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
              {overlapCount} overlaps · {gapCount} gaps · {optimizeCount} optimize · {orphanCount} orphans
            </span>
          </div>

          <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
            <h1 className="font-display font-extrabold leading-[0.86] tracking-[-0.045em] text-t-72">
              Insights
            </h1>
            <div className="flex flex-col gap-1 pb-3">
              <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted">
                Across {skills.length} skills
              </span>
              <span className="font-mono text-[12px] uppercase tracking-[0.12em] text-muted">
                {gaps?.generated ? `Generated ${gaps.generated.split("T")[0]}` : "Loading…"}
              </span>
            </div>
            <div className="ml-auto self-end flex flex-wrap gap-2">
              {tabBtn("overlaps", "Overlaps", gaps?.overlaps?.length ?? "…")}
              {tabBtn("gaps", "Gaps", gaps?.gaps?.length ?? "…")}
              {tabBtn("optimize", "Optimize", topOptimize.length)}
            </div>
          </div>

          <div className="editorial-rule mt-4" />
        </header>

        <div className="p-6">
          {gapsLoading && tab !== "optimize" && (
            <p className="font-mono text-[11px] text-muted uppercase">// loading gaps.json</p>
          )}

          {tab === "overlaps" && gaps && (
            <ul className="grid gap-3 max-w-5xl md:grid-cols-2">
              {gaps.overlaps.slice(0, 30).map((o) => {
                const pct = o.overlap_pct ?? 0;
                const cls = pct >= 60 ? "text-bad" : pct >= 30 ? "text-warn" : "text-good";
                return (
                  <li key={`${o.skill_a}-${o.skill_b}`} className="border border-fg p-4 hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[3px_3px_0_0_var(--fg)] transition-all duration-[80ms]">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      <button type="button" className="font-display font-bold text-t-20 hover:underline text-left" onClick={() => openSkill(o.skill_a)}>
                        {o.skill_a}
                      </button>
                      <span className="text-muted font-mono text-[14px]">↔</span>
                      <button type="button" className="font-display font-bold text-t-20 hover:underline text-left" onClick={() => openSkill(o.skill_b)}>
                        {o.skill_b}
                      </button>
                      <span className={`font-display font-extrabold text-t-26 tabular-nums tracking-[-0.02em] ml-auto ${cls}`}>
                        {pct}%
                      </span>
                    </div>
                    {o.shared_keywords?.length ? (
                      <p className="mt-2 font-mono text-[10px] text-muted uppercase tracking-[0.08em]">
                        // {o.shared_keywords.slice(0, 8).join(" · ")}
                      </p>
                    ) : null}
                  </li>
                );
              })}
              {gaps.overlaps.length === 0 && (
                <li className="font-mono text-[11px] text-muted uppercase">// no overlaps detected</li>
              )}
            </ul>
          )}

          {tab === "gaps" && gaps && (
            <ul className="grid gap-3 max-w-5xl md:grid-cols-2">
              {gaps.gaps.length === 0 ? (
                <li className="font-display font-extrabold text-t-40 text-good leading-none tracking-[-0.03em]">
                  No capability gaps detected.
                </li>
              ) : (
                gaps.gaps.map((g) => (
                  <li key={g.category} className="border border-fg p-4 hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[3px_3px_0_0_var(--fg)] transition-all duration-[80ms]">
                    <div className="flex items-baseline justify-between gap-3 mb-2">
                      <span className="font-display font-bold text-t-20">{g.category}</span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted">
                        {g.match_score}% match
                      </span>
                    </div>
                    <p className="text-[13px] leading-snug">{g.description}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted mt-3">
                      Closest: {g.best_match ?? "none"}
                    </p>
                  </li>
                ))
              )}
            </ul>
          )}

          {tab === "optimize" && (
            <ul className="grid gap-3 max-w-5xl md:grid-cols-2">
              {topOptimize.length === 0 ? (
                <li className="font-mono text-[11px] text-muted uppercase">
                  // Run PYTHONPATH=src python3 -m skill_grinder.cli optimize then build:data
                </li>
              ) : (
                topOptimize.map(([name, row]) => {
                  const scoreColor = row.score < 30 ? "text-bad" : row.score < 45 ? "text-warn" : "text-good";
                  return (
                    <li key={name} className="border border-fg p-4 hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[3px_3px_0_0_var(--fg)] transition-all duration-[80ms]">
                      <button
                        type="button"
                        className="w-full text-left flex justify-between items-baseline gap-3"
                        onClick={() => openSkill(name)}
                      >
                        <span className="font-display font-bold text-t-20 hover:underline">{name}</span>
                        <span className={`font-display font-extrabold text-t-26 tabular-nums tracking-[-0.02em] ${scoreColor}`}>
                          {row.score}<span className="text-[12px] text-muted">/50</span>
                        </span>
                      </button>
                      <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted mt-1">
                        // {row.suggestion_count} suggestions
                      </p>
                      <ul className="mt-3 space-y-1.5 border-t border-rule pt-3">
                        {row.suggestions.slice(0, 3).map((s, i) => (
                          <li key={i} className="font-mono text-[11px] leading-snug">
                            <span className="text-accent uppercase tracking-[0.1em] text-[9px] font-bold mr-1">[{s.priority}]</span>
                            <span className="text-fg">{s.description}</span>
                          </li>
                        ))}
                      </ul>
                    </li>
                  );
                })
              )}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}
